/**
 * Shared camera streams
 *
 * One VMS session per camera, however many places show it. A camera's frames
 * come from a detached <video> this module creates and owns; every player that
 * wants that camera renders `captureStream()` of it into its own element.
 *
 * The element is deliberately not a player's. An earlier version made the first
 * player the owner and the rest mirrors, and every grid lifecycle event then
 * became a way to break the picture: paging away tore down the session others
 * were watching and forced a visible re-buffer while someone else took over,
 * and a tap on the owning tile paused everyone at once. Here no component owns
 * the session, so none of that can happen — a player attaching or leaving never
 * touches the feed, and the feed outlives any particular view of it.
 *
 * Because this module holds the network session, recovery lives here too: a
 * player's own element does no fetching and so cannot stall on its own.
 *
 * Chrome-only by design: captureStream() is unprefixed there and the operator
 * panel targets Chrome. Firefox needs mozCaptureStream, Safari has neither.
 */

import { ref } from "vue";

/** Chrome implements this unprefixed; the DOM lib does not declare it yet. */
type CapturableVideo = HTMLVideoElement & { captureStream(): MediaStream };

export interface SharedStreamState {
    status: "connecting" | "playing" | "error";
    error: string | null;
    /**
     * True once this feed has produced real pictures. A player joining after
     * that can skip its stabilization window — the smear it guards against is
     * long past, and covering a good picture for eight seconds is worse.
     */
    hasPlayed: boolean;
    /**
     * What consumers should render. Null until the feed has a live video track,
     * and again while a recovery reload is in flight.
     */
    stream: MediaStream | null;
    /**
     * The feed gave its VMS session back and is no longer in the registry, so
     * consumers are attached to an entry nothing will revive. A consumer that
     * sees this must forget it is joined, or its Retry would release a key that
     * is gone — or worse, one a newer session has since taken.
     */
    sessionReleased: boolean;
}

/** Everything a feed needs to open and reopen itself, supplied by the caller. */
export interface SharedStreamSource {
    /** Fresh, fully-authenticated stream URL. Called again on every recovery. */
    getStreamUrl: () => Promise<string>;
    /** Force a new media token, for when the old one is why we are failing. */
    refreshToken: () => Promise<unknown>;
}

interface SharedStream {
    key: string;
    video: HTMLVideoElement;
    stream: MediaStream | null;
    source: SharedStreamSource;
    state: SharedStreamState;
    consumers: Map<string, (state: SharedStreamState) => void>;
    recoveryAttempts: number;
    recoveryTimer: number | null;
    /** A reopen is mid-flight; a second one would abort the element it waits on. */
    recovering: boolean;
    monitorTimer: number | null;
    lastProgressAt: number;
    lastCurrentTime: number;
    lastFrameCount: number;
    /** Furthest playhead of the current session, for spotting a rewind. */
    playhead: number;
    /** Kept apart from recoveryAttempts, which every reopen's 'playing' resets. */
    rewindReopens: number;
    lastRewindAt: number;
    closed: boolean;
}

/**
 * Concurrency lives here now that a session is per camera rather than per
 * player: this map is the set of live VMS sessions, so its size is the only
 * number worth limiting. Observed ceiling, not a documented server value.
 *
 * Held one below that ceiling so stills, tokens and the rest of the edge API
 * keep a slot: saturating the VMS with streams stalls those calls instead of
 * refusing them, which surfaces as tiles that never load anything.
 */
export const MAX_CONCURRENT_STREAMS = 5;

const streams = new Map<string, SharedStream>();

// Mirrors streams.size reactively: a player parked at the limit watches this to
// know when a feed closed and it can try again. A plain Map cannot be watched.
const streamCount = ref(0);

// High because the backoff is short: what bounds a feed is the window these
// attempts cover (~20s), not the count.
const MAX_RECOVERY_ATTEMPTS = 8;
const MONITOR_INTERVAL_MS = 3000;
const STALL_THRESHOLD_MS = 8000;
// Seconds the playhead may step back before it counts as a rewind.
const REWIND_TOLERANCE_S = 1;
// A link too slow for the feed rewinds it again after every reopen; back off,
// then give the slot up rather than reopen forever.
const MAX_REWIND_REOPENS = 5;
const REWIND_WINDOW_MS = 60_000;

function decodedFrames(video: HTMLVideoElement): number {
    return video.getVideoPlaybackQuality?.().totalVideoFrames ?? 0;
}

/**
 * Off-screen rather than display:none or fully detached: Chrome stops decoding
 * a video it never lays out, and a feed that decodes nothing captures nothing.
 */
function hiddenHost(): HTMLElement {
    const existing = document.getElementById("shared-camera-streams");
    if (existing) return existing;

    const host = document.createElement("div");
    host.id = "shared-camera-streams";
    host.setAttribute("aria-hidden", "true");
    host.style.cssText =
        "position:fixed;left:-10000px;top:0;width:1px;height:1px;overflow:hidden;pointer-events:none;";
    document.body.appendChild(host);
    return host;
}

/**
 * Identifies the feed itself, so two VMS servers can reuse camera ids safely.
 *
 * Playback position is part of it: a viewer scrubbed back to 10:15 must never
 * be handed the live feed of the same camera, which would quietly show it the
 * wrong footage. Live shares with live; a recorded view only shares with one
 * sitting at the identical position.
 */
export function cameraStreamKey(
    vmsId: number,
    vmsCameraId: string,
    positionMs?: number,
): string {
    const when = positionMs ? `at-${positionMs}` : "live";
    return `${vmsId}:${vmsCameraId}:${when}`;
}

/** Whether a camera is already streaming, so joining it costs no new session. */
export function isCameraStreaming(key: string): boolean {
    return streams.has(key);
}

/** Live VMS sessions, which is one per camera being watched. Reactive. */
export function activeStreamCount(): number {
    return streamCount.value;
}

/** Reactive, so a watcher fires when a feed closes and a slot opens up. */
export function hasFreeStreamSlot(): boolean {
    return streamCount.value < MAX_CONCURRENT_STREAMS;
}

function publish(entry: SharedStream, patch: Partial<SharedStreamState>): void {
    entry.state = { ...entry.state, ...patch, stream: entry.stream };
    entry.consumers.forEach((notify) => notify(entry.state));
}

/**
 * Capture the feed, once it has something to capture.
 *
 * captureStream() on an element at HAVE_NOTHING returns a MediaStream with no
 * tracks, and an element handed a trackless stream never runs its load
 * algorithm again when the tracks arrive — it just sits there showing nothing.
 * So capture on loadedmetadata, and again after a recovery reload, which ends
 * the previous capture's tracks.
 */
function ensureCapture(entry: SharedStream): void {
    if (entry.closed) return;
    if (entry.video.readyState < HTMLMediaElement.HAVE_METADATA) return;
    if (entry.stream?.getVideoTracks().some((t) => t.readyState === "live")) {
        return;
    }

    entry.stream?.getTracks().forEach((track) => track.stop());
    entry.stream = (entry.video as CapturableVideo).captureStream();
    publish(entry, {});
}

/**
 * Deadline on a single open: a VMS that accepts the connection and then sends
 * nothing fires no error, so play() would stay pending for good.
 */
const OPEN_TIMEOUT_MS = 8000;

// Opens all fire at once. Pacing them through a queue did not stop the 429s -
// the burst is refused either way - and a camera waiting its turn sat on a
// spinner long enough to be reported as broken and then recover. A refused
// open is simply retried, fast enough that the tile keeps its still picture.
// Jittered so cameras refused together do not re-fire in lockstep.
function retryDelay(attempt: number): number {
    return (
        Math.min(4_000, 250 * 2 ** Math.max(0, attempt - 1)) +
        Math.random() * 250
    );
}

// Refreshing nulls the token every camera is about to use, so parallel
// recoveries share one refresh rather than invalidating each other's.
let tokenRefresh: Promise<unknown> | null = null;
function refreshTokenOnce(source: SharedStreamSource): Promise<unknown> {
    tokenRefresh ??= source.refreshToken().finally(() => {
        tokenRefresh = null;
    });
    return tokenRefresh;
}

function withOpenDeadline(
    entry: SharedStream,
    work: Promise<void>,
): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        const timer = window.setTimeout(() => {
            // An element that has metadata is being fed; it is just slow to
            // start, and tearing it down to open it again only pays for the
            // slow part twice. Let it run - the stall monitor has it from here.
            if (entry.video.readyState >= HTMLMediaElement.HAVE_METADATA) {
                resolve();
                return;
            }

            // Abort the element so the pending play() settles and it stops
            // fetching a source nobody is waiting for any more.
            entry.video.removeAttribute("src");
            entry.video.load();
            reject(new Error("Camera feed did not start"));
        }, OPEN_TIMEOUT_MS);

        const done = (): void => clearTimeout(timer);
        work.then(
            () => {
                done();
                resolve();
            },
            (error: unknown) => {
                done();
                reject(error);
            },
        );
    });
}

/**
 * @param refreshToken Mint a fresh media token first. Worth its round trip only
 * once a plain reopen has already failed.
 */
async function openStream(
    entry: SharedStream,
    refreshToken = false,
): Promise<void> {
    if (entry.closed) return;
    if (refreshToken) {
        await refreshTokenOnce(entry.source);
        if (entry.closed) return;
    }
    await withOpenDeadline(entry, loadInto(entry));
}

/**
 * Give the VMS session and the slot it occupies back.
 *
 * Unlike releaseSharedStream this keeps the entry's consumers attached, so the
 * players watching it still receive the state that follows.
 */
function releaseSession(entry: SharedStream): void {
    entry.closed = true;
    if (entry.recoveryTimer !== null) {
        clearTimeout(entry.recoveryTimer);
        entry.recoveryTimer = null;
    }
    if (entry.monitorTimer !== null) {
        clearInterval(entry.monitorTimer);
        entry.monitorTimer = null;
    }

    entry.stream?.getTracks().forEach((track) => track.stop());
    entry.stream = null;
    entry.video.pause();
    entry.video.removeAttribute("src");
    entry.video.load();
    entry.video.remove();

    if (streams.get(entry.key) === entry) {
        streams.delete(entry.key);
        streamCount.value = streams.size;
    }
}

async function loadInto(entry: SharedStream): Promise<void> {
    const url = await entry.source.getStreamUrl();
    if (entry.closed) return;

    // load() ends the tracks of any capture already taken from this element,
    // so drop it and let consumers detach; ensureCapture() takes a fresh one
    // when the new resource reports metadata.
    //
    // hasPlayed goes with it. A reopen is a new VMS session joining mid-GOP
    // exactly as the first one did, so its opening frames are smear again -
    // left set, it told every watcher this feed was past that, and each
    // reconnect uncovered the picture on the worst frames it would show.
    if (entry.stream || entry.state.hasPlayed) {
        entry.stream?.getTracks().forEach((track) => track.stop());
        entry.stream = null;
        publish(entry, { hasPlayed: false });
    }

    entry.video.src = url;
    entry.video.load();

    entry.lastCurrentTime = 0;
    entry.lastFrameCount = 0;
    entry.playhead = 0;
    entry.lastProgressAt = Date.now();

    await entry.video.play();
}

function scheduleRecovery(entry: SharedStream, delayMs: number): void {
    if (entry.closed || entry.recoveryTimer !== null) return;

    entry.recoveryTimer = window.setTimeout(() => {
        entry.recoveryTimer = null;
        void recover(entry);
    }, delayMs);
}

/**
 * Hand the session and its slot back: a feed that stopped trying while holding
 * them shrank the pool for good. Consumers see the error; Retry builds anew.
 */
function giveUp(entry: SharedStream, message: string): void {
    releaseSession(entry);
    publish(entry, {
        status: "error",
        error: message,
        sessionReleased: true,
    });
}

function handleRewind(entry: SharedStream): void {
    const now = Date.now();
    if (now - entry.lastRewindAt > REWIND_WINDOW_MS) entry.rewindReopens = 0;
    entry.lastRewindAt = now;

    if (entry.rewindReopens >= MAX_REWIND_REOPENS) {
        giveUp(entry, "Connection too slow for this camera");
        return;
    }

    // Paused so the wait shows a frozen frame, not replayed old footage.
    entry.video.pause();
    const delay =
        entry.rewindReopens === 0 ? 0 : retryDelay(entry.rewindReopens);
    entry.rewindReopens++;
    scheduleRecovery(entry, delay);
}

async function recover(entry: SharedStream): Promise<void> {
    if (entry.closed) return;

    // An open runs for up to OPEN_TIMEOUT_MS, and the element's own 'error' and
    // 'ended' events - plus the stall monitor, whose threshold is that same
    // window - all schedule a recovery while it is still pending. Running a
    // second loadInto() then reloads the element the first is waiting on,
    // aborting a good open and burning the budget at twice the rate. Come back
    // once it has settled instead.
    if (entry.recovering) {
        scheduleRecovery(entry, retryDelay(1));
        return;
    }

    if (entry.recoveryAttempts >= MAX_RECOVERY_ATTEMPTS) {
        giveUp(entry, "Camera feed unavailable");
        return;
    }

    entry.recoveryAttempts++;
    entry.recovering = true;
    publish(entry, { status: "connecting", error: null });

    try {
        await openStream(entry, entry.recoveryAttempts > 1);
        entry.recovering = false;
    } catch (error: unknown) {
        // Cleared before scheduling, so the next attempt is not turned away by
        // the in-flight guard above.
        entry.recovering = false;
        if (entry.closed) return;
        const message = error instanceof Error ? error.message : "Stream failed";
        scheduleRecovery(entry, retryDelay(entry.recoveryAttempts));
        publish(entry, { status: "connecting", error: message });
    }
}

/**
 * Stall detection. A live feed whose currentTime stops advancing while nothing
 * reported an error is the failure mode that produces a frozen picture with no
 * indication anything is wrong.
 */
function startMonitor(entry: SharedStream): void {
    entry.monitorTimer = window.setInterval(() => {
        if (entry.closed) return;

        // Frames count too: currentTime alone misread a feed that was still
        // decoding as stalled.
        const now = entry.video.currentTime;
        const frames = decodedFrames(entry.video);
        if (now > entry.lastCurrentTime || frames > entry.lastFrameCount) {
            entry.lastCurrentTime = now;
            entry.lastFrameCount = frames;
            entry.lastProgressAt = Date.now();
            return;
        }

        if (
            !entry.video.paused &&
            Date.now() - entry.lastProgressAt > STALL_THRESHOLD_MS
        ) {
            entry.lastProgressAt = Date.now();
            scheduleRecovery(entry, 0);
        }
    }, MONITOR_INTERVAL_MS);
}

function createStream(key: string, source: SharedStreamSource): SharedStream {
    const video = document.createElement("video");
    // Muted and inline so play() is never refused for want of a user gesture —
    // nobody can click this element, so a blocked autoplay would be terminal.
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    // Required, not cosmetic: captureStream() throws on a tainted element, so a
    // feed loaded without CORS could never be shared. The edge API sends
    // Access-Control-Allow-Origin, so there is no no-CORS fallback here.
    video.crossOrigin = "anonymous";
    hiddenHost().appendChild(video);

    const entry: SharedStream = {
        key,
        video,
        stream: null,
        source,
        state: {
            status: "connecting",
            error: null,
            hasPlayed: false,
            stream: null,
            sessionReleased: false,
        },
        consumers: new Map(),
        recoveryAttempts: 0,
        recoveryTimer: null,
        recovering: false,
        monitorTimer: null,
        lastProgressAt: Date.now(),
        lastCurrentTime: 0,
        lastFrameCount: 0,
        playhead: 0,
        rewindReopens: 0,
        lastRewindAt: 0,
        closed: false,
    };

    video.addEventListener("loadedmetadata", () => ensureCapture(entry));

    // Chrome rewinds a starved feed to replay old footage as live; only a new
    // session gets back to the live edge.
    video.addEventListener("timeupdate", () => {
        if (entry.closed || entry.recovering) return;
        const now = video.currentTime;
        if (now < entry.playhead - REWIND_TOLERANCE_S) {
            entry.playhead = 0;
            handleRewind(entry);
            return;
        }
        entry.playhead = Math.max(entry.playhead, now);
    });

    video.addEventListener("playing", () => {
        entry.recoveryAttempts = 0;
        ensureCapture(entry);
        publish(entry, { status: "playing", error: null, hasPlayed: true });
    });

    video.addEventListener("error", () => {
        if (entry.closed) return;
        publish(entry, {
            status: "connecting",
            error: entry.video.error?.message || "Camera feed error",
        });
        scheduleRecovery(entry, retryDelay(entry.recoveryAttempts + 1));
    });

    // 'ended' on a live feed means the server closed it, not that we are done.
    video.addEventListener("ended", () => {
        if (entry.closed) return;
        scheduleRecovery(entry, retryDelay(entry.recoveryAttempts + 1));
    });

    startMonitor(entry);
    return entry;
}

/**
 * Join a camera's feed, opening it if nobody is watching yet.
 *
 * The MediaStream is delivered through onState rather than returned: it does
 * not exist yet when a feed is opening, and it is replaced on every recovery.
 *
 * @returns false when the concurrency limit is reached and this camera is not
 * already streaming.
 */
export async function acquireSharedStream(opts: {
    key: string;
    consumerId: string;
    source: SharedStreamSource;
    onState: (state: SharedStreamState) => void;
}): Promise<boolean> {
    const { key, consumerId, source, onState } = opts;

    const existing = streams.get(key);
    if (existing) {
        existing.consumers.set(consumerId, onState);
        onState(existing.state);
        return true;
    }

    if (!hasFreeStreamSlot()) return false;

    const entry = createStream(key, source);
    streams.set(key, entry);
    streamCount.value = streams.size;
    entry.consumers.set(consumerId, onState);
    onState(entry.state);

    // Deliberately not awaited. The open reports itself through onState, and a
    // caller left waiting on it cannot let go of its join until the VMS answers
    // - so paging away mid-open held a session nobody was watching for the
    // length of an open, and the next page hit the limit instead of starting.
    // Flagged like a recovery: the first open is just as long, and the element's
    // own events can schedule a reopen straight into it.
    entry.recovering = true;
    void openStream(entry)
        .then(() => {
            entry.recovering = false;
        })
        .catch((error: unknown) => {
            entry.recovering = false;
            if (entry.closed) return;
            const message =
                error instanceof Error ? error.message : "Stream failed";
            publish(entry, { status: "connecting", error: message });
            scheduleRecovery(entry, retryDelay(1));
        });

    return true;
}

/**
 * Leave a camera's feed. The session closes only when the last watcher goes, so
 * paging a grid past a camera a viewer is still watching costs that viewer
 * nothing.
 */
export function releaseSharedStream(key: string, consumerId: string): void {
    const entry = streams.get(key);
    if (!entry) return;

    entry.consumers.delete(consumerId);
    if (entry.consumers.size > 0) return;

    releaseSession(entry);
}

/** Sign-out: drop every feed regardless of who is still watching. */
export function resetSharedStreams(): void {
    [...streams.keys()].forEach((key) => {
        streams.get(key)?.consumers.clear();
        releaseSharedStream(key, "*");
    });
    streams.clear();
    streamCount.value = 0;
    tokenRefresh = null;
}

// Dev-only window hook: this registry is module-private, and "which cameras
// have a session, and who is watching each" is the question every failure here
// turns on.
if (import.meta.env.DEV) {
    (window as unknown as Record<string, unknown>).__cameraStreams = () =>
        [...streams.values()].map((entry) => ({
            key: entry.key,
            status: entry.state.status,
            error: entry.state.error,
            hasPlayed: entry.state.hasPlayed,
            paused: entry.video.paused,
            frames: entry.video.getVideoPlaybackQuality?.().totalVideoFrames,
            // The capture is where this breaks: a stream with no live video
            // track renders as a frozen picture with nothing else to show for it.
            tracks: entry.stream
                ? entry.stream.getTracks().map((t) => `${t.kind}:${t.readyState}`)
                : null,
            consumers: [...entry.consumers.keys()],
        }));
}
